import React from "react";
import { LucideIcon } from "lucide-react";

type GroupedProfileSectionProps = {
  title: string;
  icon: LucideIcon;
  data: Array<{
    title: string;
    items: Array<[string, any]>;
  }>;
  isEmptyValue?: (value: any) => boolean;
};

const GroupedProfileSection: React.FC<GroupedProfileSectionProps> = ({
  title,
  icon: Icon,
  data,
  isEmptyValue = (value: any) =>
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" && Object.keys(value).length === 0),
}) => {
  // Filter out empty subsections
  const validSections = data
    .map((section) => ({
      ...section,
      items: section.items.filter(
        ([, value]) =>
          value !== null &&
          value !== undefined &&
          value !== "" &&
          !isEmptyValue(value)
      ),
    }))
    .filter((section) => section.items.length > 0);

  if (validSections.length === 0) return null;

  return (
    <section className="bg-white rounded-lg shadow-2xl p-4 sm:p-6">
      <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
        <Icon className="text-pink-600" size={18} /> {title}
      </h2>

      {validSections.map((section, idx) => (
        <div key={section.title} className={idx > 0 ? "mt-6" : ""}>
          <h3 className="text-sm sm:text-base font-semibold mb-3 text-gray-700 border-b pb-2">
            {section.title}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 sm:gap-y-3 text-xs sm:text-sm">
            {section.items.map(([label, value]) => (
              <div
                key={label}
                className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-x-2 py-1.5 border-b border-gray-100"
              >
                <span className="min-w-0 text-black [overflow-wrap:anywhere]">{label}</span>
                <span aria-hidden="true" className="text-gray-600">:</span>
                <span className="min-w-0 font-medium text-left [overflow-wrap:anywhere]">
                  {String(value).trim()}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
};

export default GroupedProfileSection;
